import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { SrCrewService } from '@app/services/sr-crew.service';
import { SrResourceService, SrResourceViewGet } from '@app/services/sr-resource.service';

@Component({
    selector: 'tt-sr-resource-view',
    templateUrl: './sr-resource-view.component.html',
    styleUrls: ['./sr-resource-view.component.css'],
    standalone: false,
})
export class SrResourceViewComponent implements OnInit {
    public page?: SrResourceViewGet;
    public mainResources: Item[] = [];
    public subResources: Item[] = [];
    public items: Item[] = [];
    public error = '';
    private readonly rememberTimers = new Map<keyof SrResourceViewGet, ReturnType<typeof setTimeout>>();

    constructor(private readonly resources: SrResourceService, private readonly crew: SrCrewService, private readonly remember: RememberService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        try {
            await Promise.all([
                this.resources.getResourceView().then(async page => {
                    this.page = page;
                    await Promise.all([this.loadSubResources(), this.loadItems()]);
                }),
                this.crew.getMainResources().then(items => this.mainResources = items),
            ]);
        } catch (error) { this.showError(error); }
    }

    public async onMainResourceChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.main_resource_id = value;
        if (!value?.trim()) return;
        this.rememberField('main_resource_id');
        try { await this.loadSubResources(); } catch (error) { this.showError(error); }
    }

    public async onSubResourceChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.sub_resource_id = value;
        if (!value?.trim()) return;
        this.rememberField('sub_resource_id');
        try { await this.loadItems(); } catch (error) { this.showError(error); }
    }

    public async onDeactivatedChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.show_deactivated_resources = value;
        if (!value?.trim()) return;
        this.rememberField('show_deactivated_resources');
        try { await Promise.all([this.loadSubResources(), this.loadItems()]); } catch (error) { this.showError(error); }
    }

    public onFilterChanged(value: string): void {
        if (!this.page || value === this.page.filtervalue) return;
        this.page.filtervalue = value;
        this.rememberField('filtervalue');
    }

    private async loadSubResources(): Promise<void> {
        if (!this.page?.main_resource_id?.trim() || !this.page.show_deactivated_resources?.trim()) return;
        // Legacy keeps the previous selector options while the replacement list loads.
        this.subResources = await this.crew.getSubResources({ main_resource_id: this.page.main_resource_id, show_deactivated_resources: this.page.show_deactivated_resources });
    }

    private async loadItems(): Promise<void> {
        if (!this.page?.sub_resource_id?.trim() || !this.page.show_deactivated_resources?.trim()) return;
        this.items = [];
        this.items = await this.resources.getResourceViews({ sub_resource_id: this.page.sub_resource_id, show_deactivated_resources: this.page.show_deactivated_resources });
    }

    private rememberField(field: keyof SrResourceViewGet): void {
        const timer = this.rememberTimers.get(field);
        if (timer !== undefined) clearTimeout(timer);
        this.rememberTimers.delete(field);
        const value = this.page?.[field];
        if (!value?.trim()) return;
        // The legacy delay applies to every remembered field while filtervalue is nonempty.
        const write = () => {
            this.rememberTimers.delete(field);
            void this.remember.remember(`w_srresourceview.${field}`, value).catch(error => this.showError(error));
        };
        if (this.page!.filtervalue > '') this.rememberTimers.set(field, setTimeout(write, 1000));
        else write();
    }

    public newResource(): void {
        if (this.page) this.navigator?.go({ to: 'srresource-test', parms: { resource_id_belongto: this.page.sub_resource_id, resource_id: 'NEW' } });
    }

    public openItem(event: { item: Item; event: MouseEvent }): void {
        if (!event.item.item_state) return;
        const target = { to: event.item.item_state === 'srresource' ? 'srresource-test' : event.item.item_state, parms: event.item.item_parms };
        if (event.event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }

    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
